import type { AuthSession } from "./models/session.model";
import type { AuthTokens } from "./models/auth-tokens.model";
import { AUTH_STORAGE_KEYS } from "./auth.constants";

function resolveAuthStorage(): Storage {
  if (typeof window === "undefined") {
    return sessionStorage;
  }
  if (import.meta.env.VITE_AUTH_STORAGE === "session") {
    return sessionStorage;
  }
  return localStorage;
}

const storage = resolveAuthStorage();

/** Incrémenté à chaque purge — invalide les verifySession en cours. */
let authEpoch = 0;

export function getAuthEpoch(): number {
  return authEpoch;
}

export function isAuthEpochCurrent(epoch: number): boolean {
  return epoch === authEpoch;
}

function migrateLegacySessionStorage(): void {
  if (storage !== localStorage || typeof window === "undefined") return;
  if (localStorage.getItem(AUTH_STORAGE_KEYS.session)) return;

  const legacySession = sessionStorage.getItem(AUTH_STORAGE_KEYS.session);
  if (!legacySession) return;

  for (const key of Object.values(AUTH_STORAGE_KEYS)) {
    const value = sessionStorage.getItem(key);
    if (value !== null) {
      localStorage.setItem(key, value);
    }
  }
}

export interface StoredAuthMeta {
  accessToken: string | null;
  refreshToken: string | null;
  userId: string | null;
  tenantId: string | null;
  expiresAt: number | null;
}

export function getStoredMeta(): StoredAuthMeta {
  migrateLegacySessionStorage();
  const expiresRaw = storage.getItem(AUTH_STORAGE_KEYS.expiresAt);
  return {
    accessToken: storage.getItem(AUTH_STORAGE_KEYS.accessToken),
    refreshToken: storage.getItem(AUTH_STORAGE_KEYS.refreshToken),
    userId: storage.getItem(AUTH_STORAGE_KEYS.userId),
    tenantId: storage.getItem(AUTH_STORAGE_KEYS.tenantId),
    expiresAt: expiresRaw ? Number(expiresRaw) : null,
  };
}

export function saveSession(session: AuthSession): void {
  const { tokens, user, tenant } = session;
  storage.setItem(AUTH_STORAGE_KEYS.accessToken, tokens.accessToken);
  storage.setItem(AUTH_STORAGE_KEYS.refreshToken, tokens.refreshToken);
  storage.setItem(AUTH_STORAGE_KEYS.userId, user.id);
  storage.setItem(AUTH_STORAGE_KEYS.tenantId, tenant.id);
  storage.setItem(AUTH_STORAGE_KEYS.expiresAt, String(tokens.expiresAt));
  storage.setItem(AUTH_STORAGE_KEYS.session, JSON.stringify(session));
}

export function updateTokens(tokens: AuthTokens): void {
  storage.setItem(AUTH_STORAGE_KEYS.accessToken, tokens.accessToken);
  storage.setItem(AUTH_STORAGE_KEYS.refreshToken, tokens.refreshToken);
  storage.setItem(AUTH_STORAGE_KEYS.expiresAt, String(tokens.expiresAt));

  const raw = storage.getItem(AUTH_STORAGE_KEYS.session);
  if (!raw) return;
  try {
    const session = JSON.parse(raw) as AuthSession;
    session.tokens = tokens;
    storage.setItem(AUTH_STORAGE_KEYS.session, JSON.stringify(session));
  } catch {
    storage.removeItem(AUTH_STORAGE_KEYS.session);
  }
}

export function loadStoredSession(): AuthSession | null {
  migrateLegacySessionStorage();
  const raw = storage.getItem(AUTH_STORAGE_KEYS.session);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthSession;
  } catch {
    clearStoredSession();
    return null;
  }
}

export function clearStoredSession(): void {
  authEpoch += 1;
  Object.values(AUTH_STORAGE_KEYS).forEach((key) => storage.removeItem(key));
  if (storage === localStorage && typeof window !== "undefined") {
    Object.values(AUTH_STORAGE_KEYS).forEach((key) =>
      sessionStorage.removeItem(key),
    );
  }
}

export function isTokenExpired(expiresAt: number | null): boolean {
  if (!expiresAt) return true;
  return Date.now() >= expiresAt;
}

export function shouldRefreshToken(expiresAt: number | null): boolean {
  if (!expiresAt) return false;
  return Date.now() >= expiresAt - 5 * 60 * 1000;
}
